import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
} from "framer-motion";
import { Link } from "react-router-dom";

const ServiceCard = ({
  title,
  slug = "",
  icon,
  description,
  gradient = "from-indigo-500 to-blue-600",
  index = 0,
}) => {
  const cardRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  /* ---------------------------------------------------------
     Premium pointer interaction
     --------------------------------------------------------- */

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);

  const springRotateX = useSpring(rotateX, {
    stiffness: 220,
    damping: 24,
    mass: 0.6,
  });

  const springRotateY = useSpring(rotateY, {
    stiffness: 220,
    damping: 24,
    mass: 0.6,
  });

  const handlePointerMove = (event) => {
    if (prefersReducedMotion || !cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;

    /* Cursor spotlight */
    cardRef.current.style.setProperty(
      "--spotlight-x",
      `${percentX}%`
    );

    cardRef.current.style.setProperty(
      "--spotlight-y",
      `${percentY}%`
    );

    /* Subtle 3D tilt */
    rotateX.set(((percentY - 50) / 50) * -2);
    rotateY.set(((percentX - 50) / 50) * 2);
  };

  const handlePointerLeave = () => {
    if (!cardRef.current) return;

    rotateX.set(0);
    rotateY.set(0);

    cardRef.current.style.setProperty(
      "--spotlight-x",
      "50%"
    );

    cardRef.current.style.setProperty(
      "--spotlight-y",
      "35%"
    );
  };

  const servicePath = slug
    ? `/services/${slug}`
    : "/services";

  const number = String(index + 1).padStart(2, "0");

  return (
    <motion.article
      ref={cardRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      style={{
        rotateX: springRotateX,
        rotateY: springRotateY,
        transformPerspective: 1200,
      }}
      whileHover={
        prefersReducedMotion
          ? undefined
          : {
              y: -4,
            }
      }
      className="
        group
        relative
        flex
        h-full
        min-h-[235px]
        flex-col
        overflow-hidden
        rounded-[24px]
        border
        border-gray-200/80
        bg-white
        shadow-[0_8px_30px_rgba(15,23,42,0.055)]
        transition-[border-color,box-shadow]
        duration-300
        hover:border-gray-300
        hover:shadow-[0_20px_48px_rgba(15,23,42,0.11)]
        dark:border-gray-800
        dark:bg-gray-900
        dark:hover:border-gray-700
        dark:hover:shadow-[0_20px_48px_rgba(0,0,0,0.28)]
      "
    >
      {/* =====================================================
          Cursor Spotlight
         ===================================================== */}

      <div
        aria-hidden="true"
        className="
          pointer-events-none
          absolute
          inset-0
          z-0
          opacity-0
          transition-opacity
          duration-300
          group-hover:opacity-100
        "
        style={{
          background:
            "radial-gradient(190px circle at var(--spotlight-x, 50%) var(--spotlight-y, 35%), rgba(99,102,241,0.10), transparent 68%)",
        }}
      />

      {/* =====================================================
          Premium top border
         ===================================================== */}

      <div
        aria-hidden="true"
        className={`
          absolute
          inset-x-0
          top-0
          z-20
          h-[3px]
          bg-gradient-to-r
          ${gradient}
        `}
      />

      {/* =====================================================
          Decorative corner glow
         ===================================================== */}

      <div
        aria-hidden="true"
        className={`
          pointer-events-none
          absolute
          -right-14
          -top-14
          h-32
          w-32
          rounded-full
          bg-gradient-to-br
          ${gradient}
          opacity-0
          blur-3xl
          transition-opacity
          duration-500
          group-hover:opacity-[0.12]
        `}
      />

      {/* =====================================================
          Content
         ===================================================== */}

      <div className="relative z-10 flex h-full flex-col p-5">

        {/* Header Row */}
        <div className="flex items-start justify-between gap-4">

          {/* Icon */}
          <div
            className={`
              relative
              flex
              h-[52px]
              w-[52px]
              shrink-0
              items-center
              justify-center
              overflow-hidden
              rounded-[16px]
              bg-gradient-to-br
              ${gradient}
              text-white
              shadow-lg
              transition-transform
              duration-300
              group-hover:scale-[1.04]
            `}
          >
            {/* Inner highlight */}
            <span
              aria-hidden="true"
              className="
                absolute
                inset-0
                bg-gradient-to-br
                from-white/20
                via-transparent
                to-black/10
              "
            />

            <span
              aria-hidden="true"
              className="relative z-10 text-[22px]"
            >
              {icon}
            </span>
          </div>

          {/* Number */}
          <span
            aria-hidden="true"
            className="
              pt-1
              text-[10px]
              font-bold
              tracking-[0.18em]
              text-gray-300
              transition-colors
              duration-300
              group-hover:text-gray-400
              dark:text-gray-700
              dark:group-hover:text-gray-600
            "
          >
            {number}
          </span>
        </div>

        {/* Title */}
        <h3
          className="
            mt-5
            text-[18px]
            font-bold
            leading-[1.35]
            tracking-[-0.02em]
            text-gray-950
            dark:text-white
          "
        >
          <Link
            to={servicePath}
            className="
              rounded
              outline-none
              transition-colors
              hover:text-indigo-600
              focus-visible:ring-2
              focus-visible:ring-indigo-500
              focus-visible:ring-offset-2
              dark:hover:text-indigo-400
              dark:focus-visible:ring-offset-gray-900
            "
          >
            {title}
          </Link>
        </h3>

        {/* Description */}
        <p
          className="
            mt-2
            line-clamp-3
            min-h-[60px]
            text-[12.5px]
            leading-[1.65]
            text-gray-600
            dark:text-gray-300
          "
        >
          {description}
        </p>

        {/* Bottom area */}
        <div className="mt-auto pt-5">

          {/* Divider */}
          <div
            aria-hidden="true"
            className="
              mb-4
              h-px
              w-full
              bg-gray-100
              dark:bg-gray-800
            "
          />

          {/* Service Link */}
          <Link
            to={servicePath}
            aria-label={`Explore ${title}`}
            className="
              group/link
              flex
              min-h-10
              items-center
              justify-between
              rounded-xl
              border
              border-gray-200
              bg-gray-50/80
              px-3.5
              py-2.5
              text-[12px]
              font-bold
              text-gray-800
              transition-all
              duration-300
              hover:border-indigo-200
              hover:bg-indigo-50
              hover:text-indigo-700
              hover:shadow-sm
              focus:outline-none
              focus-visible:ring-2
              focus-visible:ring-indigo-500
              focus-visible:ring-offset-2
              dark:border-gray-700
              dark:bg-gray-800/70
              dark:text-gray-100
              dark:hover:border-indigo-800
              dark:hover:bg-indigo-950/50
              dark:hover:text-indigo-300
              dark:focus-visible:ring-offset-gray-900
            "
          >
            <span>Explore Service</span>

            <span
              aria-hidden="true"
              className="
                flex
                h-6
                w-6
                items-center
                justify-center
                rounded-full
                bg-white
                text-gray-500
                shadow-sm
                transition-all
                duration-300
                group-hover/link:translate-x-0.5
                group-hover/link:bg-indigo-600
                group-hover/link:text-white
                dark:bg-gray-900
                dark:text-gray-400
                dark:group-hover/link:bg-indigo-500
                dark:group-hover/link:text-white
              "
            >
              →
            </span>
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default ServiceCard;